import { useEffect, useRef, useState } from 'react';
import { Icon } from './Icon';

export type InlineNodeCreatorVariant = 'bar' | 'prompt';

export type InlineNodeCreatorProps = {
  /** Pill text + verb, e.g. "New Section", "New Sub-Section", "Add Subsections". */
  label: string;
  /** Accessible label for the title input shown while editing. */
  inputAriaLabel: string;
  /**
   * `bar` (default) is the hover-revealed blue insertion line that sits between
   * rows (Figma 1061-108686). `prompt` is an always-visible soft pill, used for
   * the "+ Add Subsections" affordance on an empty novel Section (1061-108006).
   */
  variant?: InlineNodeCreatorVariant;
  placeholder?: string;
  /** Disables the input + confirm while a create request is in flight. */
  pending?: boolean;
  /** Inline error (mapped from a 409/422); keeps the editor open so the user can retry. */
  error?: string | null;
  /** Client-side cap; mirrors the server's MAX_NODE_TITLE_LENGTH. */
  maxLength?: number;
  /** Called with the trimmed, non-blank title on confirm. */
  onCreate: (title: string) => void;
};

/**
 * Self-contained "add a novel node here" affordance for the assembly view. An
 * idle pill (hover-revealed blue bar, or an always-on soft prompt) swaps to an
 * inline title input on click; Enter/Confirm commits, Esc/Cancel returns to idle.
 * State is local to ui-suvoda — the parent supplies `onCreate`, `pending` and
 * `error`, and the editor closes itself once a create settles successfully.
 */
export function InlineNodeCreator({
  label,
  inputAriaLabel,
  variant = 'bar',
  placeholder = 'Name',
  pending = false,
  error = null,
  maxLength = 300,
  onCreate,
}: InlineNodeCreatorProps) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const wasPending = useRef(false);

  // Close the editor once a create request settles without error.
  useEffect(() => {
    if (wasPending.current && !pending && !error) {
      setEditing(false);
      setValue('');
    }
    wasPending.current = pending;
  }, [pending, error]);

  // Focus the input on entering edit mode.
  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const cancel = () => {
    setEditing(false);
    setValue('');
  };

  const confirm = () => {
    const trimmed = value.trim();
    if (!trimmed || pending) return;
    onCreate(trimmed);
  };

  if (editing) {
    return (
      <div className="py-1">
        <div className="flex items-center gap-2 rounded-md border border-border-focus bg-surface-card px-3 py-2">
          <input
            ref={inputRef}
            type="text"
            value={value}
            maxLength={maxLength}
            placeholder={placeholder}
            aria-label={inputAriaLabel}
            disabled={pending}
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                confirm();
              } else if (event.key === 'Escape') {
                event.preventDefault();
                cancel();
              }
            }}
            className="flex-1 border-0 bg-transparent py-1 text-md text-text-primary outline-none placeholder:text-text-secondary disabled:opacity-60"
          />
          <button
            type="button"
            aria-label="Cancel"
            onClick={cancel}
            className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-sm border border-border bg-surface-card text-text-secondary hover:bg-ink-100 hover:text-text-primary"
          >
            <Icon name="close" size={14} />
          </button>
          <button
            type="button"
            aria-label={`Confirm ${label}`}
            data-tone="confirm"
            disabled={pending || value.trim().length === 0}
            onClick={confirm}
            className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-sm border border-success-strong bg-success-strong text-brand-on-primary hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Icon name="check" size={14} />
          </button>
        </div>
        {error ? (
          <p role="alert" className="mt-1 px-1 text-xs text-danger-fg">
            {error}
          </p>
        ) : null}
      </div>
    );
  }

  if (variant === 'prompt') {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="inline-flex h-[22px] cursor-pointer items-center gap-1 rounded-pill border-0 bg-info-bg px-3 text-sm font-semibold text-text-link hover:brightness-95"
      >
        <Icon name="plus" size={12} /> {label}
      </button>
    );
  }

  return (
    // Zero-height so it never pushes rows apart; the line + pill overlay the gap
    // on hover (matching the inline "New Test Case" insertion line).
    <div className="relative h-0">
      <button
        type="button"
        aria-label={label}
        onClick={() => setEditing(true)}
        className="group/insert absolute inset-x-0 top-0 z-[3] flex h-[14px] -translate-y-1/2 cursor-pointer items-center justify-center"
      >
        <span
          aria-hidden="true"
          className="absolute inset-x-0 top-1/2 h-[2px] -translate-y-1/2 bg-[#515ffc] opacity-0 transition-opacity group-hover/insert:opacity-100 group-focus-within/insert:opacity-100"
        />
        <span className="relative z-[1] inline-flex h-[20px] items-center gap-1 rounded-pill bg-[#4c70f0] px-3 text-center font-['IBMPlexSansDevanagari'] text-[12px] font-normal leading-[1.4] tracking-[-0.15px] text-white opacity-0 transition-opacity group-hover/insert:opacity-100 group-focus-within/insert:opacity-100">
          <Icon name="plus" size={12} /> {label}
        </span>
      </button>
    </div>
  );
}
